// src/pages/ROICalculator/AssumptionsModal.jsx

import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  Divider,
  IconButton,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Close as CloseIcon,
  CheckCircle as CheckIcon,
  Info as InfoIcon,
  TrendingDown as ConservativeIcon
} from '@mui/icons-material';

import { CALCULATION_CONFIG } from './constants';

const AssumptionsModal = ({ open, onClose, assumptions }) => {
  const [expanded, setExpanded] = useState(false);

  const handleAccordionChange = (panel) => (event, isExpanded) => {
    setExpanded(isExpanded ? panel : false);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      scroll="paper"
      PaperProps={{
        sx: {
          borderRadius: 3,
          maxHeight: '90vh'
        }
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          pb: 2
        }}
      >
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5 }}>
            Calculation Assumptions
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            Complete methodology and conservative estimates
          </Typography>
        </Box>
        <IconButton onClick={onClose} sx={{ ml: 2 }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <Divider />

      <DialogContent sx={{ px: 3, py: 3 }}>
        {/* Introduction */}
        <Box
          sx={{
            p: 2,
            mb: 3,
            bgcolor: '#e8f5e9',
            borderRadius: 2,
            border: '1px solid #4caf50'
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <ConservativeIcon color="success" />
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              Conservative Estimation Approach
            </Typography>
          </Box>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            We intentionally use conservative estimates in all calculations. When research
            provides a range, we use the lower or middle value. This means your actual
            results will often <strong>exceed</strong> these projections. We'd rather
            under-promise and over-deliver than the reverse.
          </Typography>
        </Box>

        {/* Key Parameters Table */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
            Key Calculation Parameters
          </Typography>
          <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #e0e0e0' }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: '#f5f7fa' }}>
                  <TableCell sx={{ fontWeight: 600 }}>Parameter</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Value</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Source</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow>
                  <TableCell>AI Query Handling Rate</TableCell>
                  <TableCell>{(CALCULATION_CONFIG.customerService.aiHandlingRate * 100).toFixed(0)}%</TableCell>
                  <TableCell>
                    <Typography variant="caption">{CALCULATION_CONFIG.customerService.source}</Typography>
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Avg Response Time</TableCell>
                  <TableCell>{CALCULATION_CONFIG.customerService.averageResponseTime} minutes</TableCell>
                  <TableCell>
                    <Typography variant="caption">{CALCULATION_CONFIG.customerService.source}</Typography>
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Content Time Reduction</TableCell>
                  <TableCell>{(CALCULATION_CONFIG.contentCreation.aiReductionRate * 100).toFixed(0)}%</TableCell>
                  <TableCell>
                    <Typography variant="caption">{CALCULATION_CONFIG.contentCreation.source}</Typography>
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Part-Time Labor Rate</TableCell>
                  <TableCell>${CALCULATION_CONFIG.laborCosts.partTimeHourlyRate}/hr</TableCell>
                  <TableCell>
                    <Typography variant="caption">{CALCULATION_CONFIG.laborCosts.source}</Typography>
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Revenue per Star Increase</TableCell>
                  <TableCell>{(CALCULATION_CONFIG.revenueImpact.ratingImpactPerStar * 100).toFixed(0)}%</TableCell>
                  <TableCell>
                    <Typography variant="caption">{CALCULATION_CONFIG.revenueImpact.source}</Typography>
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </Box>

        {/* Assumption Categories */}
        {assumptions.map((category, categoryIndex) => (
          <Accordion
            key={categoryIndex}
            expanded={expanded === `panel${categoryIndex}`}
            onChange={handleAccordionChange(`panel${categoryIndex}`)}
            sx={{
              mb: 2,
              borderRadius: 2,
              '&:before': { display: 'none' },
              boxShadow: 1,
              '&.Mui-expanded': {
                boxShadow: 3
              }
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              sx={{
                bgcolor: '#f5f7fa',
                borderRadius: expanded === `panel${categoryIndex}` ? '8px 8px 0 0' : 2,
                '&:hover': {
                  bgcolor: '#e3f2fd'
                }
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <InfoIcon color="primary" />
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    {category.category}
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                    {category.items.length} assumption{category.items.length > 1 ? 's' : ''}
                  </Typography>
                </Box>
              </Box>
            </AccordionSummary>

            <AccordionDetails sx={{ pt: 3 }}>
              {category.items.map((item, itemIndex) => (
                <Box
                  key={itemIndex}
                  sx={{
                    mb: itemIndex < category.items.length - 1 ? 3 : 0,
                    pb: itemIndex < category.items.length - 1 ? 3 : 0,
                    borderBottom:
                      itemIndex < category.items.length - 1
                        ? '1px solid'
                        : 'none',
                    borderColor: 'divider'
                  }}
                >
                  {/* Assumption Statement */}
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 1 }}>
                    <CheckIcon
                      sx={{
                        color: item.conservative ? 'success.main' : 'info.main',
                        mt: 0.5,
                        fontSize: 20
                      }}
                    />
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                        {item.assumption}
                      </Typography>
                      <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1 }}>
                        {item.rationale}
                      </Typography>

                      {/* Conservative Badge */}
                      {item.conservative && (
                        <Chip
                          label="Conservative Estimate"
                          size="small"
                          color="success"
                          icon={<ConservativeIcon />}
                          sx={{ mr: 1 }}
                        />
                      )}

                      {/* Note if present */}
                      {item.note && (
                        <Alert severity="info" sx={{ mt: 1 }}>
                          <Typography variant="caption">{item.note}</Typography>
                        </Alert>
                      )}
                    </Box>
                  </Box>
                </Box>
              ))}
            </AccordionDetails>
          </Accordion>
        ))}

        {/* Methodology Notes */}
        <Box sx={{ mt: 3 }}>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
            Calculation Methodology
          </Typography>

          <Box
            sx={{
              p: 2,
              mb: 2,
              bgcolor: '#f5f7fa',
              borderRadius: 2,
              border: '1px solid #e0e0e0'
            }}
          >
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
              Time Savings Formula:
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary', fontFamily: 'monospace', mb: 1 }}>
              Customer Service Hours = (Messages/Day × 3 min × 80% AI handling) / 60
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary', fontFamily: 'monospace', mb: 1 }}>
              Content Creation Hours = 7.5 hrs/week × 70% reduction
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary', fontFamily: 'monospace' }}>
              Review Management Hours = (Monthly Reviews × 8 min × 75%) / 60
            </Typography>
          </Box>

          <Box
            sx={{
              p: 2,
              mb: 2,
              bgcolor: '#f5f7fa',
              borderRadius: 2,
              border: '1px solid #e0e0e0'
            }}
          >
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
              Cost Savings Formula:
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary', fontFamily: 'monospace', mb: 1 }}>
              Labor Savings = Hours Saved × ($18/hr × 1.25 benefits multiplier)
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary', fontFamily: 'monospace' }}>
              Tool Savings = $117 traditional tools - $49 Revfer = $68/month
            </Typography>
          </Box>

          <Box
            sx={{
              p: 2,
              bgcolor: '#f5f7fa',
              borderRadius: 2,
              border: '1px solid #e0e0e0'
            }}
          >
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
              Revenue Impact Formula:
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary', fontFamily: 'monospace', mb: 1 }}>
              Rating Revenue = Monthly Revenue × Rating Increase × 6% per star
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary', fontFamily: 'monospace', mb: 1 }}>
              Response Revenue = New Customers × Avg Transaction Value
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary', fontFamily: 'monospace' }}>
              Smart Rating Boost = 0.1-0.4 stars (based on current rating & review count)
            </Typography>
          </Box>
        </Box>

        {/* Disclaimer */}
        <Alert severity="warning" sx={{ mt: 3 }}>
          <Typography variant="body2">
            <strong>Important:</strong> These calculations provide estimates based on industry
            averages and conservative assumptions. Your actual results will depend on your
            specific business operations, implementation quality, customer engagement, and
            market conditions. Past performance and research data do not guarantee future results.
          </Typography>
        </Alert>

        {/* When to Update */}
        <Box
          sx={{
            mt: 3,
            p: 2,
            bgcolor: '#fff3cd',
            borderRadius: 2,
            border: '1px solid #ffc107'
          }}
        >
          <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
            📊 Data Maintenance Schedule
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            We review and update our calculation parameters quarterly as new research becomes
            available. Last methodology review: {new Date().toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}. 
            Next scheduled review: {new Date(new Date().setMonth(new Date().getMonth() + 3)).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}.
          </Typography>
        </Box>
      </DialogContent>

      <Divider />

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} variant="contained" sx={{ textTransform: 'none' }}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AssumptionsModal;